import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabaseClient'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const ACCESS_FILTERS = [
  { value: 'all', label: 'Všetci' },
  { value: 'active', label: 'Aktívni' },
  { value: 'none', label: 'Bez prístupu' },
  { value: 'expired', label: 'Expirovaný' },
]

const formatDate = (iso) => {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return '—'
  }
}

// Default valid_until = 31 May of the current Slovak school year.
// School year ends 31 May; if today is in Jun-Dec, the active school year
// ends on 31 May of the following calendar year.
const defaultValidUntilForToday = () => {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth() + 1 // 1..12
  const endYear = m <= 5 ? y : y + 1
  return `${endYear}-05-31`
}

const StatusBadge = ({ status }) => {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
        Aktívny
      </span>
    )
  }
  if (status === 'expired') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 ring-1 ring-amber-200">
        Expirovaný
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-50 text-slate-700 ring-1 ring-slate-200">
      Bez prístupu
    </span>
  )
}

const AdminUsers = () => {
  const navigate = useNavigate()
  const { user, isAdmin, adminChecked } = useAuth()
  const [users, setUsers] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [accessFilter, setAccessFilter] = useState('all')

  // ---- Grant access modal state ----
  const [grantTarget, setGrantTarget] = useState(null)         // user object or null
  const [grantUntil, setGrantUntil] = useState(defaultValidUntilForToday())
  const [grantReason, setGrantReason] = useState('')
  const [granting, setGranting] = useState(false)
  const [grantError, setGrantError] = useState('')
  const [toast, setToast] = useState(null)                     // { type, message }
  const [togglingTestId, setTogglingTestId] = useState(null)   // user id currently mid-toggle

  const toggleTestAccount = async (u) => {
    // Safe, minimal mutation: flips ONLY user_profiles.is_test_account
    // via /api/admin/users/{id}/test-account. Does NOT touch auth, role,
    // email, password or program_access.
    const nextValue = !u.is_test_account
    const confirmMsg = nextValue
      ? `Označiť „${u.email}" ako testovací účet?\n\nTento účet bude vylúčený z hromadných e-mailov a zo všetkých agregovaných štatistík.`
      : `Zrušiť označenie „testovací účet" pre „${u.email}"?\n\nÚčet sa opäť započíta do štatistík a hromadných e-mailov.`
    if (!window.confirm(confirmMsg)) return

    setTogglingTestId(u.id)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${u.id}/test-account`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_test_account: nextValue }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || 'Nepodarilo sa zmeniť príznak testovacieho účtu.')
      }
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_test_account: nextValue } : x)))
      setToast({ type: 'success', message: nextValue ? 'Účet označený ako testovací.' : 'Označenie testovacieho účtu zrušené.' })
    } catch (e) {
      setToast({ type: 'error', message: e.message || 'Chyba pri zmene príznaku.' })
    } finally {
      setTogglingTestId(null)
    }
  }

  const openGrantModal = (u) => {
    setGrantTarget(u)
    setGrantUntil(defaultValidUntilForToday())
    setGrantReason('')
    setGrantError('')
  }

  const closeGrantModal = () => {
    if (granting) return
    setGrantTarget(null)
    setGrantError('')
  }

  const submitGrant = async () => {
    if (!grantTarget) return
    setGrantError('')
    if (!grantUntil) {
      setGrantError('Zadajte dátum platnosti.')
      return
    }
    if (!grantReason.trim()) {
      setGrantError('Zadajte dôvod udelenia prístupu (interná poznámka).')
      return
    }
    try {
      setGranting(true)
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) {
        setGrantError('Autentifikácia zlyhala.')
        return
      }
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users/${grantTarget.id}/grant-access`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_until: grantUntil,
            reason: grantReason.trim(),
          }),
        },
      )
      if (!response.ok) {
        let msg = `HTTP ${response.status}`
        try {
          const j = await response.json()
          msg = j.detail || msg
        } catch {
          /* ignore */
        }
        setGrantError(msg)
        return
      }
      const result = await response.json()
      setToast({
        type: 'success',
        message: `Prístup udelený používateľovi ${result.email} (do ${formatDate(result.valid_until)}).`,
      })
      setGrantTarget(null)
      // Refresh table
      loadUsers(search, accessFilter)
    } catch (err) {
      console.error('[AdminUsers] grant error:', err)
      setGrantError('Nepodarilo sa udeliť prístup.')
    } finally {
      setGranting(false)
    }
  }

  // Auto-dismiss toast after 5s
  useEffect(() => {
    if (!toast) return undefined
    const t = setTimeout(() => setToast(null), 5000)
    return () => clearTimeout(t)
  }, [toast])

  const loadUsers = useCallback(async (s, f) => {
    try {
      setLoading(true)
      setError('')

      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) {
        setError('Autentifikácia zlyhala')
        return
      }

      const params = new URLSearchParams()
      if (s) params.set('search', s)
      if (f && f !== 'all') params.set('access_filter', f)

      const url = `${BACKEND_URL}/api/admin/users-overview${params.toString() ? `?${params}` : ''}`
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!response.ok) {
        const detail = await response.text().catch(() => '')
        throw new Error(detail || `HTTP ${response.status}`)
      }
      const data = await response.json()
      setUsers(data.users || [])
      setTotal(data.total || 0)
    } catch (err) {
      console.error('[AdminUsers] load error:', err)
      setError('Nepodarilo sa načítať používateľov')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!user || !adminChecked || !isAdmin) return
    loadUsers(search, accessFilter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, adminChecked, isAdmin])

  // Debounced search
  useEffect(() => {
    if (!user || !adminChecked || !isAdmin) return
    const t = setTimeout(() => loadUsers(search, accessFilter), 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, accessFilter])

  if (!adminChecked) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Overujem prístup…</p>
        </div>
      </div>
    )
  }

  if (adminChecked && !isAdmin) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 ring-1 ring-slate-100 shadow-sm p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Prístup zamietnutý</h2>
          <p className="text-sm text-gray-600 mb-4">Táto stránka je dostupná iba administrátorom.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition"
          >
            Späť na Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent">
      <div className="container mx-auto px-4 py-8 md:py-10">
        {/* Intro card */}
        <section
          className="bg-gradient-to-br from-white via-blue-50/30 to-emerald-50/20 border border-slate-200 ring-1 ring-slate-100 rounded-2xl shadow-md shadow-slate-200/40 p-6 sm:p-8 mb-6 md:mb-8"
          data-testid="admin-users-intro"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => navigate('/admin')}
                  className="group inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-blue-700 bg-white ring-1 ring-blue-200 hover:bg-blue-50 hover:ring-blue-300 hover:shadow-sm rounded-full shadow-sm transition-all duration-200"
                  data-testid="admin-users-back"
                >
                  <svg className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Admin Panel
                </button>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Používatelia
              </h1>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl">
                Prehľad zaregistrovaných používateľov, ich prístupu k programu a počtu dokončených testov. Iba na čítanie.
              </p>
            </div>

            <div className="flex-shrink-0">
              <span className="inline-flex items-center px-3.5 py-1.5 bg-slate-50 text-slate-700 ring-1 ring-slate-200 rounded-full font-medium text-sm">
                Celkovo: <strong className="ml-1 text-slate-900">{total}</strong>
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-6 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Hľadať podľa e-mailu…"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white text-sm text-slate-800 placeholder-slate-400 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                data-testid="admin-users-search"
              />
            </div>

            <div className="flex flex-wrap gap-1.5" data-testid="admin-users-filters">
              {ACCESS_FILTERS.map((f) => {
                const active = accessFilter === f.value
                return (
                  <button
                    key={f.value}
                    onClick={() => setAccessFilter(f.value)}
                    className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      active
                        ? 'bg-blue-600 text-white ring-1 ring-blue-700/30 shadow-sm'
                        : 'bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200'
                    }`}
                    data-testid={`admin-users-filter-${f.value}`}
                  >
                    {f.label}
                  </button>
                )
              })}
            </div>
          </div>
        </section>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50 ring-1 ring-rose-100 rounded-2xl">
            <p className="text-rose-700 text-sm">{error}</p>
          </div>
        )}

        {/* Table card */}
        <section
          className="bg-white border border-slate-100 ring-1 ring-slate-50 rounded-2xl shadow-sm overflow-hidden"
          data-testid="admin-users-card"
        >
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-600 text-sm">Načítavam používateľov…</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center" data-testid="admin-users-empty">
              <span aria-hidden="true" className="inline-flex w-12 h-12 mb-3 rounded-2xl bg-slate-100 text-slate-400 items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zM21 12a4 4 0 10-8 0 4 4 0 008 0z" />
                </svg>
              </span>
              <h3 className="font-semibold text-gray-900 mb-1">Žiadni používatelia</h3>
              <p className="text-sm text-gray-600">
                Skúste zmeniť hľadaný výraz alebo vybrať iný filter.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="text-left font-semibold px-5 py-3">E-mail</th>
                      <th className="text-left font-semibold px-5 py-3">Registrácia</th>
                      <th className="text-left font-semibold px-5 py-3">Stav prístupu</th>
                      <th className="text-left font-semibold px-5 py-3">Platí do</th>
                      <th className="text-left font-semibold px-5 py-3">Šk. rok</th>
                      <th className="text-left font-semibold px-5 py-3">Zdroj</th>
                      <th className="text-right font-semibold px-5 py-3">Dokončené testy</th>
                      <th className="text-right font-semibold px-5 py-3">Akcie</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50/50 transition" data-testid={`admin-users-row-${u.id}`}>
                        <td className="px-5 py-3">
                          <div className="font-medium text-gray-900 break-all">{u.email}</div>
                          {u.display_name && (
                            <div className="text-xs text-gray-500">{u.display_name}</div>
                          )}
                          {u.role && u.role !== 'student' && (
                            <span className="inline-flex mt-1 mr-1 items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 ring-1 ring-blue-200">
                              {u.role}
                            </span>
                          )}
                          {u.is_test_account && (
                            <span
                              className="inline-flex mt-1 items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-purple-50 text-purple-700 ring-1 ring-purple-200"
                              data-testid={`test-account-badge-${u.id}`}
                              title="Vylúčený z hromadných e-mailov a agregovaných štatistík."
                            >
                              Testovací účet
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{formatDate(u.created_at)}</td>
                        <td className="px-5 py-3"><StatusBadge status={u.access_status} /></td>
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{u.access_valid_until ? formatDate(u.access_valid_until) : '—'}</td>
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{u.access_school_year || '—'}</td>
                        <td className="px-5 py-3 text-gray-600 capitalize whitespace-nowrap">{u.access_source || '—'}</td>
                        <td className="px-5 py-3 text-right font-semibold text-gray-900">{u.completed_tests}</td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            <button
                              onClick={() => toggleTestAccount(u)}
                              disabled={togglingTestId === u.id}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ring-1 transition disabled:opacity-50 disabled:cursor-not-allowed ${
                                u.is_test_account
                                  ? 'text-purple-700 bg-purple-50 ring-purple-200 hover:bg-purple-100'
                                  : 'text-gray-600 bg-gray-50 ring-gray-200 hover:bg-gray-100'
                              }`}
                              data-testid={`admin-users-toggle-test-${u.id}`}
                              title={u.is_test_account ? 'Zrušiť označenie testovacieho účtu' : 'Označiť ako testovací účet'}
                            >
                              {togglingTestId === u.id ? '…' : (u.is_test_account ? 'Zrušiť testovací' : 'Označiť ako test')}
                            </button>
                            <button
                              onClick={() => openGrantModal(u)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-blue-700 bg-blue-50 ring-1 ring-blue-200 hover:bg-blue-100 hover:ring-blue-300 transition"
                              data-testid={`admin-users-grant-${u.id}`}
                            >
                              {u.access_status === 'active' ? 'Predĺžiť' : 'Udeliť prístup'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile list */}
              <ul className="md:hidden divide-y divide-slate-100">
                {users.map((u) => (
                  <li key={u.id} className="p-4" data-testid={`admin-users-row-mobile-${u.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 break-all text-sm">{u.email}</div>
                        {u.display_name && (
                          <div className="text-xs text-gray-500 mt-0.5">{u.display_name}</div>
                        )}
                        {u.is_test_account && (
                          <span
                            className="inline-flex mt-1 items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-purple-50 text-purple-700 ring-1 ring-purple-200"
                            data-testid={`test-account-badge-mobile-${u.id}`}
                          >
                            Testovací účet
                          </span>
                        )}
                      </div>
                      <StatusBadge status={u.access_status} />
                    </div>
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                      <dt className="text-gray-500">Registrácia</dt>
                      <dd className="text-gray-800">{formatDate(u.created_at)}</dd>
                      <dt className="text-gray-500">Platí do</dt>
                      <dd className="text-gray-800">{u.access_valid_until ? formatDate(u.access_valid_until) : '—'}</dd>
                      <dt className="text-gray-500">Šk. rok</dt>
                      <dd className="text-gray-800">{u.access_school_year || '—'}</dd>
                      <dt className="text-gray-500">Zdroj</dt>
                      <dd className="text-gray-800 capitalize">{u.access_source || '—'}</dd>
                      <dt className="text-gray-500">Dokončené testy</dt>
                      <dd className="text-gray-900 font-semibold">{u.completed_tests}</dd>
                    </dl>
                    <button
                      onClick={() => openGrantModal(u)}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-full text-xs font-semibold text-blue-700 bg-blue-50 ring-1 ring-blue-200 hover:bg-blue-100 transition"
                      data-testid={`admin-users-grant-mobile-${u.id}`}
                    >
                      {u.access_status === 'active' ? 'Predĺžiť prístup' : 'Udeliť prístup'}
                    </button>
                    <button
                      onClick={() => toggleTestAccount(u)}
                      disabled={togglingTestId === u.id}
                      className={`mt-2 w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-full text-xs font-semibold ring-1 transition disabled:opacity-50 ${
                        u.is_test_account
                          ? 'text-purple-700 bg-purple-50 ring-purple-200 hover:bg-purple-100'
                          : 'text-gray-700 bg-gray-50 ring-gray-200 hover:bg-gray-100'
                      }`}
                      data-testid={`admin-users-toggle-test-mobile-${u.id}`}
                    >
                      {togglingTestId === u.id ? '…' : (u.is_test_account ? 'Zrušiť označenie testovacieho účtu' : 'Označiť ako testovací účet')}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 max-w-md px-4 py-3 rounded-2xl shadow-lg ring-1 ${
            toast.type === 'success'
              ? 'bg-emerald-50 ring-emerald-200 text-emerald-800'
              : 'bg-rose-50 ring-rose-200 text-rose-800'
          }`}
          data-testid="admin-users-toast"
          role="status"
        >
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              {toast.type === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              )}
            </svg>
            <p className="text-sm leading-snug">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              aria-label="Zatvoriť oznámenie"
              className="ml-2 text-current opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Grant Access Modal */}
      {grantTarget && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={closeGrantModal}
          data-testid="admin-users-grant-modal"
        >
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-blue-50 via-white to-emerald-50/40 px-6 py-5 border-b border-slate-100">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                {grantTarget.access_status === 'active' ? 'Predĺžiť prístup' : 'Udeliť prístup'}
              </h2>
              <p className="text-sm text-gray-600 break-all">
                {grantTarget.email}
              </p>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 text-xs text-amber-900 leading-snug">
                Týmto používateľovi udelíte EduMisia prístup ručne (ako admin). Stripe platba sa nevytvára ani neoznačuje ako uhradená — toto je oddelené od reálnych platieb.
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1.5" htmlFor="grant-until">
                  Platnosť prístupu do
                </label>
                <input
                  id="grant-until"
                  type="date"
                  value={grantUntil}
                  onChange={(e) => setGrantUntil(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="admin-users-grant-until"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Predvolene 31. máj prebiehajúceho školského roka.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1.5" htmlFor="grant-reason">
                  Dôvod (interná poznámka) <span className="text-rose-600">*</span>
                </label>
                <textarea
                  id="grant-reason"
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  placeholder="Napr.: Potvrdená platba bankou, automatická aktivácia zlyhala — tiket #1234."
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  data-testid="admin-users-grant-reason"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Uloží sa do auditu spolu s vaším adminom a dátumom.
                </p>
              </div>

              {grantError && (
                <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-4 py-2.5 text-sm text-rose-800">
                  {grantError}
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={closeGrantModal}
                disabled={granting}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 bg-white ring-1 ring-slate-200 hover:bg-slate-100 disabled:opacity-60"
                data-testid="admin-users-grant-cancel"
              >
                Zrušiť
              </button>
              <button
                onClick={submitGrant}
                disabled={granting}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-b from-blue-600 to-indigo-600 shadow-sm shadow-blue-500/25 ring-1 ring-blue-700/30 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="admin-users-grant-confirm"
              >
                {granting && (
                  <span aria-hidden="true" className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                )}
                {granting ? 'Udeľujem…' : 'Potvrdiť udelenie'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUsers
